import { useColorMode } from '@chakra-ui/react'
import { selectAuthenticatedUser, useSelector } from '@missionlabs/api'
import {
    Drawer,
    DrawerBody,
    DrawerContent,
    DrawerHeader,
    DrawerOverlay,
    DrawerProps,
    Spinner,
    useToast
} from '@missionlabs/react'
import { SpeedDial } from '@missionlabs/types'
import { FC, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetSpeedDialForClientQuery, useGetSpeedDialForUserQuery } from 'shared/store'

import { SpeedDialForm } from './SpeedDialForm'

export interface AddEditNumberDrawerProps extends Omit<DrawerProps, 'children'> {
    editing?: boolean
    isAdmin: boolean
}

export const AddEditNumberDrawer: FC<AddEditNumberDrawerProps> = ({
    isOpen,
    onClose,
    editing,
    isAdmin,
    ...props
}) => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { toast } = useToast()

    const { colorMode } = useColorMode()

    const completeClose = useCallback(() => {
        onClose()
        navigate(`/${isAdmin ? 'admin' : 'settings'}/speed-dial`)
    }, [navigate, onClose, isAdmin])

    const { speedDialCode = '' } = useParams()
    const { userID = '' } = useSelector(selectAuthenticatedUser) ?? {}

    const {
        data: clientSpeedDial,
        isLoading: isLoadingClientSpeedDial,
        error: errorClientSpeedDial
    } = useGetSpeedDialForClientQuery({ speedDialCode }, { skip: !editing || !userID || !isAdmin })

    const {
        data: userSpeedDial,
        isLoading: isLoadingUserSpeedDial,
        error: errorUserSpeedDial
    } = useGetSpeedDialForUserQuery(
        { userID, speedDialCode },
        { skip: !editing || !userID || isAdmin }
    )

    const { speedDial, isLoading, error } = useMemo(() => {
        if (isAdmin) {
            return {
                speedDial: clientSpeedDial,
                isLoading: isLoadingClientSpeedDial,
                error: errorClientSpeedDial
            }
        }
        return {
            speedDial: userSpeedDial,
            isLoading: isLoadingUserSpeedDial,
            error: errorUserSpeedDial
        }
    }, [
        clientSpeedDial,
        isAdmin,
        isLoadingClientSpeedDial,
        errorClientSpeedDial,
        isLoadingUserSpeedDial,
        userSpeedDial,
        errorUserSpeedDial
    ])

    const defaultFormValues = useMemo(() => {
        return {
            speedDialCode: editing ? speedDial?.speedDialCode : '',
            name: editing ? speedDial?.name : '',
            replacementNumber: editing ? speedDial?.replacementNumber : ''
        } as SpeedDial
    }, [speedDial, editing])

    useEffect(() => {
        if (error) {
            console.error(error)
            // @ts-ignore
            const title = error?.message
            toast({ status: 'error', title: title || 'Failed to load speed dial' })
            completeClose()
        }
    }, [error, toast, completeClose])

    return (
        <Drawer
            {...props}
            size="sm"
            isOpen={isOpen}
            onClose={completeClose}
            onCloseComplete={completeClose}
        >
            <DrawerOverlay />
            <DrawerContent>
                <DrawerHeader>
                    {editing
                        ? t('settings.speedDial.editTitle')
                        : t('settings.speedDial.createTitle')}
                </DrawerHeader>
                <DrawerBody bgColor={`${colorMode}.tones.ghostWhite`}>
                    {isLoading || !defaultFormValues ? (
                        <Spinner />
                    ) : (
                        <SpeedDialForm
                            mode={editing ? 'edit' : 'add'}
                            values={defaultFormValues}
                            onClose={completeClose}
                        ></SpeedDialForm>
                    )}
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    )
}
